import { Box, Typography, Tab, Tabs, LinearProgress, useTheme, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useState, useMemo, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { organizationApi } from 'src/api/organizationApi';
import { mindtimeUsersApi } from 'src/api/mindtime/usersApi';
import { getPageTitle } from 'src/config/projectConfig';
import CustomizedTable from 'src/components/tables/CustomizedTable';
import { SearchField } from 'src/common/search/search-field';
import { Button, CreateUserModal, CreateUserFormValues } from 'src/common';
import {
  MindtimeUsersTableRow,
  SubOrgUserApiResponse,
} from 'src/types/mindtime/users';
import { MINDTIME_PATHS, USER_TABS } from 'src/constants/mindtime/constants';

const languageLabelByValue: Record<string, string> = {
  'en-US': 'English (US)',
  'nl-NL': 'Dutch (NL)',
};
const countryLabelByValue: Record<string, string> = {
  NL: 'Netherlands',
  DE: 'Germany',
  BE: 'Belgium',
  US: 'USA',
  GB: 'UK',
};

const resolveBackendErrorMessage = (error: any): string => {
  const errorData = error?.response?.data;

  if (typeof errorData?.message === 'string' && errorData.message.trim()) {
    return errorData.message;
  }

  if (Array.isArray(errorData?.message) && errorData.message.length > 0) {
    return String(errorData.message[0]);
  }

  if (errorData && typeof errorData === 'object') {
    const nestedMessage = Object.values(errorData as Record<string, unknown>)
      .map((value) => (value as { message?: string | string[] })?.message)
      .find(
        (message) =>
          (typeof message === 'string' && message.trim()) ||
          (Array.isArray(message) && message.length > 0)
      );

    if (typeof nestedMessage === 'string') {
      return nestedMessage;
    }

    if (Array.isArray(nestedMessage) && nestedMessage.length > 0) {
      return String(nestedMessage[0]);
    }
  }

  if (typeof error?.message === 'string' && error.message.trim()) {
    return error.message;
  }

  return 'Failed to create organization';
};

const Users = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [usersData, setUsersData] = useState<MindtimeUsersTableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState<{ field?: string; order?: 'asc' | 'desc' }>({});

  const calculateQuotaPercent = (
    quotaUsedGB?: number,
    quotaTotalGB?: number
  ): number => {
    const used = Number(quotaUsedGB || 0);
    const total = Number(quotaTotalGB || 0);
    if (total <= 0) {
      return 0;
    }

    const percentage = (used / total) * 100;
    return Math.min(100, Math.max(0, Math.round(percentage)));
  };

  const mapApiResponseToTableRow = (
    item: SubOrgUserApiResponse,
    index: number
  ): MindtimeUsersTableRow => ({
    id: item.organization?.id || `row-${index}`,
    company: item.organization?.name || '-',
    loginName: item.ahsayUser?.LoginName || '-',
    quotaPercent: calculateQuotaPercent(
      item?.quotaUsedGB,
      item?.quotaTotalGB
    ),
    quotaUsedGB: item?.quotaUsedGB,
    quotaTotalGB: item?.quotaTotalGB,
    accountType: item.ahsayUser?.UserType || '-',
  });

  useEffect(() => {
    let isMounted = true;

    const loadUsers = async () => {
      setLoading(true);
      try {
        const response = await mindtimeUsersApi.getSubOrgUsers();
        if (!isMounted) return;

        const mappedRows = (response || []).map(mapApiResponseToTableRow);
        setUsersData(mappedRows);
      } catch (error: any) {
        if (!isMounted) return;
        toast.error(
          resolveBackendErrorMessage(error) || 'Failed to load users list'
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadUsers();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleCreateUserSubmit = async (values: CreateUserFormValues) => {
    const payload = {
      firstName: values.firstName,
      lastName: values.lastName,
      designation: values.designation,
      language: languageLabelByValue[values.language] || values.language,

      productId: values.productId,
      productSKU: values.productSKU,
      numberOfDevices: Number(values.numberOfDevices || 0),
      description: values.description,

      organizationName: values.organizationName,
      organizationTypeId: values.organizationTypeId,
      website: values.website,
      domainName: values.domainName,

      street: values.street,
      city: values.city,
      province: values.province,
      country: countryLabelByValue[values.country] || values.country,
      postalCode: values.postalCode,
      timeZone: values.timeZone,

      telephone: values.telephone,
      email: values.email,
    };

    try {
      await organizationApi.createSubOrganizationFromUserModal(payload);
      toast.success('Organization created successfully');
    } catch (error: any) {
      toast.error(resolveBackendErrorMessage(error));
      throw error;
    }
  };

  const filteredData = useMemo(() => {
    if (!searchValue) return usersData;
    const lower = searchValue.toLowerCase();
    return usersData.filter(
      (u) =>
        String(u.company).toLowerCase().includes(lower) ||
        String(u.loginName).toLowerCase().includes(lower)
    );
  }, [searchValue, usersData]);

  const handleSort = (field: string) => {
    setSort((prevSort) =>
      prevSort.field === field && prevSort.order === 'desc'
        ? {}
        : {
            field,
            order:
              prevSort.field === field && prevSort.order === 'asc' ? 'desc' : 'asc',
          }
    );
    setPage(0);
  };

  const sortedData = useMemo(() => {
    if (!sort.field || !sort.order) {
      return filteredData;
    }

    const direction = sort.order === 'asc' ? 1 : -1;
    return [...filteredData].sort((a, b) => {
      const aValue = a[sort.field as keyof MindtimeUsersTableRow];
      const bValue = b[sort.field as keyof MindtimeUsersTableRow];

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * direction;
      }

      return (
        String(aValue ?? '').localeCompare(String(bValue ?? ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * direction
      );
    });
  }, [filteredData, sort]);

  const paginatedData = useMemo(
    () => sortedData.slice(page * limit, (page + 1) * limit),
    [sortedData, page, limit]
  );

  const columns = [
    {
      field: 'company',
      headerName: 'COMPANY',
      sortable: true,
      width: '30%',
    },
    {
      field: 'loginName',
      headerName: 'LOGIN NAME',
      sortable: true,
      width: '25%',
    },
    {
      field: 'quotaPercent',
      headerName: 'QUOTA / STATUS',
      sortable: false,
      width: '200px',
      render: (_value: unknown, row: MindtimeUsersTableRow) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Box flex={1}>
            <Tooltip
              title={
                <Box>
                  <Typography variant="caption" display="block">
                    Used: {Number(row.quotaUsedGB || 0).toFixed(2)} GB
                  </Typography>
                  <Typography variant="caption" display="block">
                    Total: {Number(row.quotaTotalGB || 0).toFixed(2)} GB
                  </Typography>
                  <Typography variant="caption" display="block">
                    {Number(row.quotaPercent || 0)}% used
                  </Typography>
                </Box>
              }
              arrow
              placement="top"
            >
              <LinearProgress
                variant="determinate"
                value={Number(row.quotaPercent) || 0}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: '#E2E8F0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: theme.palette.primary.main,
                  },
                }}
              />
            </Tooltip>
          </Box>
          <CheckCircleIcon sx={{ fontSize: 16, color: '#38B2AC' }} />
        </Box>
      ),
    },
    {
      field: 'accountType',
      headerName: 'ACCOUNT TYPE',
      sortable: true,
      width: '20%',
      render: (value: unknown) => (
        <Typography variant="body2" fontSize={12}>
          {String(value || '-')}
        </Typography>
      ),
    },
  ];

  const handleRowClick = (row: MindtimeUsersTableRow) => {
    const organizationId = String(row.id || '').trim();
    const targetPath = `${MINDTIME_PATHS.USERS_BACKUPS}${
      organizationId
        ? `?organizationId=${encodeURIComponent(organizationId)}`
        : ''
    }`;
    navigate(targetPath);
  };

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Backup')}</title>
      </Helmet>

      <Box p={3}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h4" fontWeight={600}>
            Backup
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="contained"
              color="primary"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minWidth: 'max-content',
              }}
              onClick={() => setIsCreateUserModalOpen(true)}
            >
              <AddIcon sx={{ fontSize: 18 }} />
              Create new client user
            </Button>
            <SearchField
              placeholder="Search ..."
              value={searchValue}
              onChange={(val) => {
                setSearchValue(val);
                setPage(0);
              }}
            />
          </Box>
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ mb: 2, borderBottom: '1px solid #EDF2F7' }}
        >
          {USER_TABS.map((t) => (
            <Tab
              key={t}
              label={t}
              sx={{ textTransform: 'none', fontWeight: 500, fontSize: 13 }}
            />
          ))}
        </Tabs>

        <CustomizedTable
          columns={columns}
          data={paginatedData}
          page={page}
          limit={limit}
          totalRecords={sortedData.length}
          handlePage={setPage}
          handleLimit={(newLimit) => {
            setLimit(newLimit);
            setPage(0);
          }}
          loading={loading}
          handleSort={handleSort}
          sort={sort}
          handleOnClick={handleRowClick}
          storageKey="mindtime-users-table"
        />

        <CreateUserModal
          open={isCreateUserModalOpen}
          onClose={() => setIsCreateUserModalOpen(false)}
          onSubmit={handleCreateUserSubmit}
          modalTitle="Create Client User"
          submitLabel="Create Client User"
          userInformationTitle="Client User Information"
        />
      </Box>
    </>
  );
};

export default Users;
